import React from 'react';
import i18n from '../../../i18n';
import { SecondMenu } from '../../../models/second-menu.entity';
import { TaxGroup } from '../../../models/tax-group.entity';
import { MenuDish } from '../../../models/menu-dish.entity';
import GlobalNotification from '../../../component/common/snackbar';
import { apiManage_rbp } from '../../../request';
import _ from 'lodash';
import DialogComponent from '../../../component/dialog/dialog.component';
import { Box, Button, CircularProgress, Paper } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { ReactComponent as BaselineDeleteForeverIcon } from '../../../static/images/svg/ic_baseline-delete-forever.svg';
import { ReactComponent as SaveIcon } from '../../../static/images/svg/ic_round-save.svg';
import ExtraCostDishDetail from './extra-cost-dish-detail.component';
import {
    VirtualizedMuiTable,
    type VirtualizedMuiTableColumn,
} from '../../../component/table/virtualized-mui-table.component';
import { getCurrencyName } from '../../../constants/tool.fn';
import AlertDialog from '../../../component/dialog/alert-dialog.component';
import '../../menu-library-management/scss/menu-library-menu-detail.style.scss';
import '../../menu-library-management/scss/menu-library-create-menu-dialog.style.scss';
import '../../menu-library-management/scss/menu-library-add-subcategory-dialog.style.scss';
import './scss/extra-cost.style.scss';

class ExtraCostDetail extends React.Component<Props, State> {
    private readonly dishFormRef = React.createRef<ExtraCostDishDetail>();

    public state: State = {
        dishBatchDeleteConfirmOpen: false,
        dishSelectedKeys: [],
        menuDishData: undefined,
        dishSaveSubmitting: false,
    };

    addMenuDish() {
        this.setState({
            menuDishData: {
                slu_id: this.props.detail?.dmi_slu_id,
            } as MenuDish,
        });
    }

    private closeDishDialog = () => {
        if (this.state.dishSaveSubmitting) {
            return;
        }
        this.setState({ menuDishData: undefined, dishSaveSubmitting: false });
    };

    private handleDishSave = async () => {
        if (this.state.dishSaveSubmitting) {
            return;
        }
        this.setState({ dishSaveSubmitting: true });
        try {
            await this.dishFormRef.current?.submitDish();
        } finally {
            this.setState({ menuDishData: undefined, dishSaveSubmitting: false });
        }
    };


    private buildDishTableColumns(): VirtualizedMuiTableColumn<MenuDish>[] {
        const variant = this.props.variant ?? 'extra-cost';
        const menuTimeColumn: VirtualizedMuiTableColumn<MenuDish> = {
            id: 'menu_time',
            header: i18n.t('extra_cost_dish_table_menu_time') + '(min)',
            renderCell: (row) => {
                const v = row.menu_time;
                if (v === null || v === undefined || v === '') {
                    return '-';
                }
                return String(v);
            },
            width: '100px',
        };

        return [
            {
                id: 'item_id',
                header: i18n.t('google_text_id'),
                renderCell: (row) => (row.item_id != null ? String(row.item_id) : '-'),
                width: '80px',
            },
            {
                id: 'dish_name',
                header: i18n.t('menu_and_dishes_page_item_name1'),
                align: 'left',
                renderCell: (row) => row.item_name1 ?? '—',
                width: '200px',
            },
            {
                id: 'dish_name2',
                header: i18n.t('menu_and_dishes_page_dish_name2'),
                align: 'left',
                renderCell: (row) => row.item_name2 ?? '—',
                width: '200px',
            },
            ...(variant === 'park-project-binding' ? [menuTimeColumn] : []),
            {
                id: 'dine_in',
                header: i18n.t('menu_and_dishes_page_price'),
                width: '140px',
                renderCell: (row) => (
                    <>
                        {getCurrencyName()} {row.price_1 ?? '—'}
                    </>
                ),
            },
            {
                id: 'takeout',
                header: i18n.t('menu_and_dishes_page_takeout_price'),
                width: '140px',
                renderCell: (row) => (
                    <>
                        {getCurrencyName()} {row.takeout_price_1 ?? '—'}
                    </>
                ),
            },
            {
                id: 'tax',
                header: i18n.t('tax_group_page_label'),
                width: '120px',
                renderCell: (row) => _.get(row as Record<string, unknown>, 'tax_group_name', '—') as string,
            },
            {
                id: 'detail',
                align: 'right',
                width: '120px',
                header: i18n.t('google_text_check_detail'),
                renderCell: (row) => (
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                            this.setState({ menuDishData: { ...row } });
                        }}
                        sx={{ textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                        {i18n.t('menu_library_detail_view_detail')}
                    </Button>
                ),
            },
        ];
    }

    async handleDeleteDishes() {
        const sluId = this.props.detail.dmi_slu_id as number;
        const deletePayload = (this.state.dishSelectedKeys as number[])
            .filter((id) => typeof id === 'number')
            .map((item_id) => ({ item_id, slu_id: sluId }));
        if (deletePayload.length === 0) {
            return;
        }
        const result = await apiManage_rbp.delete_dishes(deletePayload);
        if (!_.get(result, 'error')) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await this.props.updateMenuData();
            this.setState({
                dishSelectedKeys: [],
                dishBatchDeleteConfirmOpen: false,
            });
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }

    render() {
        const { menuDishData, dishSelectedKeys, dishBatchDeleteConfirmOpen, dishSaveSubmitting } = this.state;
        const dishes = (_.get(this.props.detail, 'menu_item') || []) as MenuDish[];
        const detailSelectedCount = dishSelectedKeys.filter((k) => typeof k === 'number').length;
        const detailMenuHasDishSelection = detailSelectedCount > 0;

        const variant = this.props.variant ?? 'extra-cost';
        return (
            <div
                className={
                    variant === 'park-project-binding'
                        ? 'extra-cost-detail extra-cost-detail--park-dish-table'
                        : 'extra-cost-detail'
                }
            >
                <Box
                    className="menu-library-menu-detail extra-cost-detail__dish-area-shell"
                    sx={{
                        /** 须给出确定主尺寸，否则内层 flex:1 + Virtuoso height:100% 会变成 0 高，列表空白 */
                        minHeight: 420,
                        height: 'min(70vh, 720px)',
                        maxHeight: 'min(70vh, 720px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Paper elevation={0} className="menu-library-menu-detail__right">
                        <Box className="menu-library-menu-detail__dish-table-stack">
                            <Box className="menu-library-menu-detail__dish-table-host extra-cost-detail__dish-table-host">
                                <VirtualizedMuiTable<MenuDish>
                                    columns={this.buildDishTableColumns()}
                                    rows={dishes}
                                    loading={false}
                                    loadingText={i18n.t('general_text_loading')}
                                    emptyText={i18n.t('general_text_no_or_more_data')}
                                    getRowKey={(row, index) => (row.item_id != null ? row.item_id : `dish-${index}`)}
                                    height="100%"
                                    tableClassName="extra-cost-detail__dish-virtual-table"
                                    checkboxSelection={{
                                        selectedKeys: dishSelectedKeys,
                                        onChange: (keys) => this.setState({ dishSelectedKeys: keys }),
                                        isRowSelectable: (row) => row.item_id != null,
                                    }}
                                />
                            </Box>
                            <Box className="menu-library-menu-detail__dish-add-row">
                                {detailMenuHasDishSelection ? (
                                    <Box className="menu-library-create-dialog menu-library-menu-detail__dish-footer-host">
                                        <Paper
                                            elevation={0}
                                            variant="outlined"
                                            className="menu-library-create-dialog__dish-batch-bar menu-library-menu-detail__dish-batch-bar"
                                            sx={{ justifyContent: 'center !important' }}
                                        >
                                            <Button
                                                type="button"
                                                variant="text"
                                                className="menu-library-create-dialog__dish-batch-btn menu-library-create-dialog__dish-batch-btn--danger menu-library-menu-detail__dish-batch-btn"
                                                startIcon={(
                                                    <BaselineDeleteForeverIcon
                                                        className="menu-library-menu-detail__dish-batch-svg-icon"
                                                        aria-hidden
                                                    />
                                                )}
                                                onClick={() => this.setState({ dishBatchDeleteConfirmOpen: true })}
                                            >
                                                {i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                            </Button>
                                        </Paper>
                                        <AlertDialog
                                            open={dishBatchDeleteConfirmOpen}
                                            variant="delete"
                                            size="sm"
                                            title={i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                            bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                                            highlightText={i18n.t(
                                                'menu_library_create_dialog_batch_delete_confirm_highlight',
                                                { count: detailSelectedCount },
                                            )}
                                            confirmText={i18n.t('menu_library_create_dialog_batch_delete_dishes')}
                                            onCancel={() => this.setState({ dishBatchDeleteConfirmOpen: false })}
                                            onConfirm={() => {
                                                this.setState({ dishBatchDeleteConfirmOpen: false });
                                                void this.handleDeleteDishes();
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="text"
                                        color="primary"
                                        fullWidth
                                        className="menu-library-menu-detail__dish-add-btn"
                                        onClick={() => this.addMenuDish()}
                                        endIcon={<AddRoundedIcon aria-hidden className="menu-library-menu-detail__dish-add-btn-icon" />}
                                    >
                                        {i18n.t('menu_library_create_dialog_add_dish')}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                <DialogComponent
                    className="menu-library-add-subcategory-dialog-root"
                    open={Boolean(menuDishData)}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    disableEscapeKeyDown={dishSaveSubmitting}
                    onClose={this.closeDishDialog}
                    defaultHeaderProps={{
                        title: menuDishData?.item_id
                            ? i18n.t('extra_cost_page_edit_ext_cost')
                            : i18n.t('extra_cost_page_add_ext_cost'),
                        onClose: this.closeDishDialog,
                    }}
                    footer={(
                        <Box className="menu-library-add-subcategory-dialog__footer menu-library-add-subcategory-dialog__footer--full-width">
                            <Button
                                type="button"
                                variant="contained"
                                color="inherit"
                                disableElevation
                                className="menu-library-add-subcategory-dialog__btn-cancel"
                                disabled={dishSaveSubmitting}
                                onClick={this.closeDishDialog}
                            >
                                {i18n.t('menu_library_detail_add_subcategory_dialog_cancel')}
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                className="menu-library-add-subcategory-dialog__btn-save"
                                disabled={dishSaveSubmitting}
                                startIcon={
                                    dishSaveSubmitting ? (
                                        <CircularProgress
                                            className="menu-library-add-subcategory-dialog__save-icon"
                                            size={28}
                                            thickness={4}
                                            color="inherit"
                                            aria-hidden
                                        />
                                    ) : (
                                        <SaveIcon
                                            className="menu-library-add-subcategory-dialog__save-icon"
                                            aria-hidden
                                        />
                                    )
                                }
                                onClick={() => void this.handleDishSave()}
                            >
                                {i18n.t('menu_library_detail_save_changes')}
                            </Button>
                        </Box>
                    )}
                >
                    {menuDishData ? (
                        <ExtraCostDishDetail
                            ref={this.dishFormRef}
                            key={menuDishData.item_id != null ? String(menuDishData.item_id) : 'new-dish'}
                            variant={this.props.variant ?? 'extra-cost'}
                            detail={menuDishData}
                            handleClickBack={this.closeDishDialog}
                            getMenuData={async () => this.props.updateMenuData()}
                            taxGroup={this.props.taxGroup}
                            printGroup={this.props.printGroup}
                        />
                    ) : null}
                </DialogComponent>
            </div>
        );
    }
}

export default ExtraCostDetail;

/** 额外费用页与游乐园项目绑定页共用详情区；游乐园需展示/编辑「时长」 */
export type ExtraCostDetailVariant = 'extra-cost' | 'park-project-binding';

type Props = {
    detail: SecondMenu;
    updateMenuData: () => Promise<void>;
    taxGroup: TaxGroup[];
    printGroup: any[];
    /** 默认 `extra-cost`；游乐园项目绑定传 `park-project-binding` 显示「时长」列与表单 */
    variant?: ExtraCostDetailVariant;
};
type State = {
    menuDishData?: MenuDish;
    dishSelectedKeys: React.Key[];
    dishBatchDeleteConfirmOpen: boolean;
    dishSaveSubmitting: boolean;
};
